/**
 * useSpacesSwitcher — shared state and handlers for SpacesSwitcher (both phone
 * pill bar and desktop tab strip variants).
 *
 * Immutability: all mutations route through the actions layer.
 */
import { useState, useCallback } from 'react';
import { MAX_SPACES } from 'briefpane/schema';
import type { Space } from 'briefpane/schema';
import type { SpacesActions, SpacesActionError } from '../board/use-spaces.js';

export type PromptMode =
  | { kind: 'create' }
  | { kind: 'rename'; spaceId: string; currentName: string }
  | null;

export interface PopoverState {
  readonly spaceId: string;
  readonly anchorRect: DOMRect;
}

export interface RenameInlineState {
  readonly spaceId: string;
  readonly currentName: string;
}

export interface SpacesSwitcherLogic {
  // State
  promptMode: PromptMode;
  promptError: string | null;
  popover: PopoverState | null;
  renameInlineState: RenameInlineState | null;
  inlineRenameError: string | null;

  // Handlers
  handlePromptConfirm(name: string): void;
  handlePromptCancel(): void;
  openPopover(spaceId: string, anchorEl: HTMLElement): void;
  handlePopoverRename(spaceId: string, currentName: string): void;
  handlePopoverDuplicate(spaceId: string): void;
  handlePopoverDelete(spaceId: string): void;
  closePopover(): void;

  // Inline rename (desktop)
  startInlineRename(spaceId: string, currentName: string): void;
  commitInlineRename(value: string): void;
  cancelInlineRename(): void;
  /** Clear only the error message without disturbing an active rename. */
  clearInlineRenameError(): void;

  // Tab IDs for ARIA
  tabId(spaceId: string): string;
  panelId: string;

  // Setters
  setPromptMode(m: PromptMode): void;
  setPromptError(e: string | null): void;
  setPopover(s: PopoverState | null): void;
}

export function useSpacesSwitcher(
  _spaces: readonly Space[],
  _activeSpaceId: string,
  actions: SpacesActions,
): SpacesSwitcherLogic {
  const [promptMode, setPromptMode] = useState<PromptMode>(null);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [renameInlineState, setRenameInlineState] = useState<RenameInlineState | null>(null);
  const [inlineRenameError, setInlineRenameError] = useState<string | null>(null);

  const handlePromptConfirm = useCallback((name: string): void => {
    if (!promptMode) return;
    let err: SpacesActionError = null;
    if (promptMode.kind === 'create') {
      err = actions.createSpace(name);
    } else {
      // Popover rename path — method='popover'.
      err = actions.renameSpace(promptMode.spaceId, name, 'popover');
    }
    if (err === 'duplicate-name') {
      setPromptError('That name is already taken. Try a different one.');
      return;
    }
    if (err === 'max-spaces') {
      setPromptError(`You've reached the limit of ${MAX_SPACES} spaces.`);
      return;
    }
    if (err !== null) {
      setPromptError('Something went wrong. Please try again.');
      return;
    }
    setPromptMode(null);
    setPromptError(null);
  }, [promptMode, actions]);

  const handlePromptCancel = useCallback((): void => {
    setPromptMode(null);
    setPromptError(null);
  }, []);

  const openPopover = useCallback((spaceId: string, anchorEl: HTMLElement): void => {
    setPopover({ spaceId, anchorRect: anchorEl.getBoundingClientRect() });
  }, []);

  const handlePopoverRename = useCallback((spaceId: string, currentName: string): void => {
    setPopover(null);
    setPromptMode({ kind: 'rename', spaceId, currentName });
    setPromptError(null);
    // Note: the rename event fires in handlePromptConfirm with method='popover'.
  }, []);

  const handlePopoverDuplicate = useCallback((spaceId: string): void => {
    setPopover(null);
    actions.duplicateSpace(spaceId);
  }, [actions]);

  const handlePopoverDelete = useCallback((spaceId: string): void => {
    setPopover(null);
    actions.deleteSpace(spaceId);
  }, [actions]);

  const closePopover = useCallback((): void => {
    setPopover(null);
  }, []);

  const startInlineRename = useCallback((spaceId: string, currentName: string): void => {
    setRenameInlineState({ spaceId, currentName });
    setInlineRenameError(null);
  }, []);

  const commitInlineRename = useCallback((value: string): void => {
    if (!renameInlineState) return;
    const trimmed = value.trim();
    if (!trimmed) {
      // Empty — cancel without error
      setRenameInlineState(null);
      setInlineRenameError(null);
      return;
    }
    // Inline rename path — method='inline'.
    const err = actions.renameSpace(renameInlineState.spaceId, trimmed, 'inline');
    if (err === 'duplicate-name') {
      setInlineRenameError('That name is already taken.');
      setRenameInlineState(null);
      return;
    }
    if (err !== null) {
      setInlineRenameError('Could not rename space.');
      setRenameInlineState(null);
      return;
    }
    setRenameInlineState(null);
    setInlineRenameError(null);
  }, [renameInlineState, actions]);

  const cancelInlineRename = useCallback((): void => {
    setRenameInlineState(null);
    setInlineRenameError(null);
  }, []);

  const clearInlineRenameError = useCallback((): void => {
    setInlineRenameError(null);
  }, []);

  const tabId = useCallback((spaceId: string): string => {
    return `space-tab-${spaceId}`;
  }, []);

  return {
    promptMode,
    promptError,
    popover,
    renameInlineState,
    inlineRenameError,
    handlePromptConfirm,
    handlePromptCancel,
    openPopover,
    handlePopoverRename,
    handlePopoverDuplicate,
    handlePopoverDelete,
    closePopover,
    startInlineRename,
    commitInlineRename,
    cancelInlineRename,
    clearInlineRenameError,
    tabId,
    panelId: 'board-panel',
    setPromptMode,
    setPromptError,
    setPopover,
  };
}
